import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Contact from '../components/Contact';

const backgroundImages = [
  'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
  'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
  'https://images.unsplash.com/photo-1518611012118-696072aa579a?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80',
  'https://images.unsplash.com/photo-1594381898411-846e7d193883?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'
];

const faqs = [
  {
    category: 'Memberships',
    backgroundPosition: 'center',
    questions: [
      {
        q: 'What membership options are available?',
        a: 'We offer several membership tiers including Entry, Gateway, and Transporter memberships. Each tier comes with different benefits and access levels. We also offer flexible payment options and both term and no-lock-in contracts.'
      },
      {
        q: 'Is there a joining fee?',
        a: 'Yes, there is a $49 joining fee plus a $25 key tag fee. Some promotions may include fee waivers.'
      },
      {
        q: 'Can I freeze my membership?',
        a: 'Yes, you can freeze your membership for up to 4 weeks per year with a valid reason (medical, travel, etc.). A small weekly suspension fee applies.'
      },
      {
        q: 'What is the cancellation policy?',
        a: 'For flexible memberships, we require 30 days written notice for cancellation. Term memberships cannot be cancelled before the term ends unless special circumstances apply.'
      }
    ]
  },
  {
    category: 'Facilities & Services',
    backgroundPosition: 'center',
    questions: [
      {
        q: 'What are your opening hours?',
        a: 'We are open Monday to Friday 5:00am - 10:00pm, and weekends 7:00am - 7:00pm.'
      },
      {
        q: 'What equipment and facilities do you have?',
        a: 'Our facility features state-of-the-art cardio and strength equipment, free weights area, functional training zone, 25m pool, sauna, steam room, ice bath, and luxury changing rooms.'
      },
      {
        q: 'Do you have parking?',
        a: 'Yes, we offer free parking for members in our secure underground car park.'
      },
      {
        q: 'Are towels provided?',
        a: 'Complimentary towel service is included with our Gateway and Transporter memberships. Other members can hire towels for a small fee or bring their own.'
      }
    ]
  },
  {
    category: 'Classes & Training',
    backgroundPosition: 'center',
    questions: [
      {
        q: 'How do I book classes?',
        a: 'Classes can be booked through our mobile app or website up to 7 days in advance. We recommend booking early as popular classes fill up quickly.'
      },
      {
        q: 'What types of classes do you offer?',
        a: 'We offer a wide range of classes including HIIT, strength, yoga, pilates, boxing, spin, and more. Check our class schedule for the full timetable.'
      },
      {
        q: 'Do you offer personal training?',
        a: 'Yes, we have a team of qualified personal trainers available for one-on-one or small group training. Initial consultation is complimentary.'
      },
      {
        q: 'What happens if I miss a class?',
        a: 'Please cancel at least 2 hours before class start time to avoid a late cancellation fee. No-shows may incur a penalty.'
      }
    ]
  },
  {
    category: 'General Information',
    backgroundPosition: 'center',
    questions: [
      {
        q: 'What should I bring to the gym?',
        a: 'We recommend bringing comfortable workout clothes, appropriate footwear, a water bottle, and a towel if you don\'t plan to hire one. Padlocks are available for purchase if needed.'
      },
      {
        q: 'Is there a minimum age requirement?',
        a: 'Yes, members must be at least 14 years old. Members aged 14-17 require parent/guardian consent.'
      },
      {
        q: 'Do you offer student discounts?',
        a: 'No, we do not currently offer student discounts.'
      },
      {
        q: 'How do I get started?',
        a: 'You can start by booking a tour of our facility or signing up for a trial pass. Our team will guide you through the membership options and help you choose the best fit.'
      }
    ]
  }
];

export default function FAQPage() {
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [openQuestions, setOpenQuestions] = useState<Record<string, boolean>>({});
  const [hoveredCategory, setHoveredCategory] = useState<number | null>(null);
  const { scrollY, scrollYProgress } = useScroll();
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleQuestion = (categoryIndex: number, questionIndex: number) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenQuestions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1571902943202-507ec2618e8f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80')] bg-cover bg-center opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/50 to-black" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h1 className="text-7xl font-bold mb-4">FAQ</h1>
              <p className="text-xl text-gray-400">Find answers to commonly asked questions</p>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-6">
              {faqs.map((category, categoryIndex) => {
                const yProgress = (scrollPosition - categoryIndex * 200) / 1000;
                const parallaxOffset = Math.min(Math.max(yProgress, 0), 1) * 50;

                return (
                  <motion.div
                    key={categoryIndex}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8 }}
                    className="relative group"
                    onMouseEnter={() => setHoveredCategory(categoryIndex)}
                    onMouseLeave={() => setHoveredCategory(null)}
                  >
                    <motion.div
                      className="relative bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10"
                      style={{
                        y: useTransform(scrollYProgress, 
                          [categoryIndex * 0.1, (categoryIndex + 1) * 0.1],
                          [0, 20]
                        )
                      }}
                    >
                      {/* Dynamic Background with Parallax */}
                      <motion.div
                        className="absolute inset-0 -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 overflow-hidden"
                        style={{
                          y: parallaxOffset
                        }}
                      >
                        <motion.div
                          className="absolute inset-0 scale-110"
                          style={{
                            backgroundImage: `url(${backgroundImages[categoryIndex]})`,
                            backgroundPosition: category.backgroundPosition,
                            backgroundSize: 'cover',
                            y: useTransform(scrollYProgress, [0, 1], ['0%', '10%'])
                          }}
                        />
                        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
                      </motion.div>

                      {/* Category Header */}
                      <button
                        className="w-full px-8 py-6 flex items-center justify-between relative z-10"
                        onClick={() => setOpenCategory(openCategory === category.category ? null : category.category)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-2 rounded-full">
                            <HelpCircle className="w-6 h-6 text-blue-400" />
                          </div>
                          <h2 className="text-2xl font-bold">{category.category}</h2>
                        </div>
                        <ChevronDown
                          className={`w-6 h-6 transition-transform duration-300 ${
                            openCategory === category.category ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      {/* Questions */}
                      {openCategory === category.category && (
                        <div className="px-8 pb-6 relative z-10">
                          <div className="space-y-4">
                            {category.questions.map((faq, questionIndex) => (
                              <motion.div
                                key={questionIndex}
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: questionIndex * 0.1 }}
                                className="border-t border-white/10 pt-4"
                              >
                                <button
                                  className="w-full flex items-center justify-between text-left group/question"
                                  onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                                >
                                  <h3 className="font-semibold pr-8 group-hover/question:text-blue-400 transition-colors">
                                    {faq.q}
                                  </h3>
                                  <ChevronDown
                                    className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${
                                      openQuestions[`${categoryIndex}-${questionIndex}`] ? 'rotate-180' : ''
                                    }`}
                                  />
                                </button>
                                {openQuestions[`${categoryIndex}-${questionIndex}`] && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    transition={{ duration: 0.3 }}
                                    className="mt-4"
                                  >
                                    <div className="relative overflow-hidden rounded-xl">
                                      <div 
                                        className="absolute inset-0 bg-cover bg-center opacity-10"
                                        style={{
                                          backgroundImage: `url(${backgroundImages[categoryIndex]})`
                                        }}
                                      />
                                      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />
                                      <p className="relative z-10 text-gray-200 p-6">
                                        {faq.a}
                                      </p>
                                    </div>
                                  </motion.div>
                                )}
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <Contact />
      </main>

      <Footer />
    </div>
  );
}